/**
 * passing_craft.ts — other travelers' engines (2026-09-20). PURE.
 *
 * Your own hovercraft roars; another player's, crossing your path at cruise,
 * made no sound at all. This is the policy for giving the nearest few a voice:
 * WHICH craft (the nearest `max`, held in sticky slots so a loop never jumps
 * from one hull to another), HOW LOUD (this file's distance curve — the panner
 * adds direction only, the one-distance-owner law), and AT WHAT PITCH (engine
 * load by speed × a Doppler shift by the radial speed — Web Audio removed its
 * own Doppler in 2016, and a fly-by without one reads as a hovering speaker).
 *
 * The loops are the same two clips your own craft uses, on the vehicles bus at
 * a low priority: the first thing a busy mix sheds.
 */
import { inverseDistanceGain, SPEED_OF_SOUND_MPS } from './spatial_math.js';

export type CraftKind = 'hover' | 'ship' | 'freighter';
export interface CraftSeen { id: string; kind: CraftKind; x: number; y: number; z: number; vx: number; vy: number; vz: number }
export interface CraftVoice extends CraftSeen { slot: number; dist: number; gain: number; rate: number }

export const PASSING_CRAFT = {
  max: 3,
  /** Two more slots than voices: a newcomer nearly always finds one that has already gone quiet. */
  slots: 5,
  /** A freed slot's loop fades for 0.35 s — it is not re-homed before that (or the sound TELEPORTS from one hull to another). */
  slotCoolS: 0.4,
  /** Past this the craft is not asked for at all. */
  rangeM: { hover: 140, ship: 420, freighter: 700 } as Record<CraftKind, number>,
  /** Full level inside this. */
  refM: { hover: 10, ship: 35, freighter: 60 } as Record<CraftKind, number>,
  /** Speed at which the engine reads as flat out (m/s) — the scaled cruise of each regime. */
  topMps: { hover: 45, ship: 70, freighter: 40 } as Record<CraftKind, number>,
  key: { hover: 'sfx_hover_engine', ship: 'sfx_ship_engine', freighter: 'sfx_freighter_engine' } as Record<CraftKind, string>,
  /** What plays while a kind's own loop is not installed (a freighter without its drone still has an engine). */
  fallbackKey: { freighter: 'sfx_ship_engine' } as Partial<Record<CraftKind, string>>,
} as const;

/** c / (c + v_r): v_r > 0 receding (lower), < 0 closing (higher). Clamped — a teleport must not squeal. */
export function dopplerRate(radialMps: number): number {
  const r = SPEED_OF_SOUND_MPS / (SPEED_OF_SOUND_MPS + Math.max(-120, Math.min(120, radialMps)));
  return Math.min(1.22, Math.max(0.82, r));
}

/** Gain + playback rate for one craft heard from (lx, ly, lz). An idling craft still hums; a parked one (speed 0) is quiet, not silent. */
export function craftMix(c: CraftSeen, lx: number, ly: number, lz: number): { dist: number; gain: number; rate: number } {
  const dx = c.x - lx, dy = c.y - ly, dz = c.z - lz;
  const dist = Math.hypot(dx, dy, dz);
  const speed = Math.hypot(c.vx, c.vy, c.vz);
  const load = Math.min(1, speed / PASSING_CRAFT.topMps[c.kind]);
  const radial = dist > 0.5 ? (c.vx * dx + c.vy * dy + c.vz * dz) / dist : 0; // + = moving away from the listener
  // A freighter is a heavy hauler: it never idles quiet, and its pitch barely moves with speed.
  const base = c.kind === 'hover' ? 0.05 + load * 0.22 : c.kind === 'ship' ? 0.07 + load * 0.24 : 0.16 + load * 0.16;
  const pitch = c.kind === 'freighter' ? 0.8 + load * 0.15 : 0.85 + load * 0.4;
  return { dist, gain: base * inverseDistanceGain(dist, PASSING_CRAFT.refM[c.kind], PASSING_CRAFT.rangeM[c.kind]), rate: pitch * dopplerRate(radial) };
}

export interface CraftSlot { id: string | null; freedAtS: number }

/**
 * The nearest `max` craft in range, each in a STICKY slot: a craft that holds a slot keeps it while it stays among the
 * chosen. A newcomer takes a slot only once that slot has COOLED (its old loop has faded out) — until then it simply
 * waits a tick or two, unvoiced: a late engine is inaudible, an engine that jumps across the sky is not.
 * `slots` is last tick's state, mutated to this tick's.
 */
export function pickPassingCraft(seen: readonly CraftSeen[], lx: number, ly: number, lz: number, slots: CraftSlot[], nowS: number): CraftVoice[] {
  while (slots.length < PASSING_CRAFT.slots) slots.push({ id: null, freedAtS: -1e9 });
  const mixed = seen.map((c) => ({ c, m: craftMix(c, lx, ly, lz) })).filter((e) => e.m.gain > 0).sort((a, b) => a.m.dist - b.m.dist).slice(0, PASSING_CRAFT.max);
  const chosen = new Set(mixed.map((e) => e.c.id));
  for (const s of slots) if (s.id !== null && !chosen.has(s.id)) { s.id = null; s.freedAtS = nowS; }
  const out: CraftVoice[] = [];
  for (const e of mixed) {
    let slot = slots.findIndex((s) => s.id === e.c.id);
    if (slot < 0) {
      // The slot that has been quiet the LONGEST, if any has cooled.
      let best = -1;
      for (let i = 0; i < slots.length; i++) if (slots[i].id === null && nowS - slots[i].freedAtS >= PASSING_CRAFT.slotCoolS && (best < 0 || slots[i].freedAtS < slots[best].freedAtS)) best = i;
      if (best < 0) continue;
      slots[best].id = e.c.id;
      slot = best;
    }
    out.push({ ...e.c, slot, ...e.m });
  }
  return out;
}
